import { isSameDay } from "date-fns";
import {
  call,
  createAction,
  safe,
  select,
  takeEvery,
  takeLeading,
} from "starfx";

import { dateToSqlDate } from "@app/service/helpers";
import {
  EComponentStack,
  EMenuCmdTab,
  SPV_RIGHT_TABS,
} from "@app/state/constants";
import {
  EUIIMainSOT,
  TUIInvoiceOverall,
} from "@app/statefx/schema/uiInvoiceMain.def";
import { select_apiProf_issuersArray } from "@app/statefx/selectors/invoiceIssuers.sel";
import { provideNodeData } from "@app/statefx/xThunks/ds-invoice";
import {
  TContractorFormSource,
  TIvFiscal,
  TRepoSPVMessages,
} from "@app/types/insider";

import { schema } from "../schema";
import { selectSpvDataOverall } from "../selectors/invoiceOverall.sel";
import { fxInvoiceTrackTreeData } from "../selectors/uiMonthTree";
import { xThunk } from "../xapis";

import type {
  TInvoceFormCreatePayload,
  TCallInvoice,
} from "@app/types/insider";
import type { ThunkCtx, Next } from "starfx";

export const uiSendClick_withSyncOption = createAction(`uiButtons/sendClick`);

export const showDateFFWithState = createAction(
  "uiInvoiceMain/showDateFFWithState"
);

export const swapInvoiceViewMode = createAction("uiInvoiceMain/dateFFSwapView");

export const fxInvoiceOverallSetCtorMirror =
  xThunk.create<TContractorFormSource>(
    "/thks/react/selectedMirroredContractor2",
    { supervisor: takeLeading },
    function* (ctx: ThunkCtx, next: Next) {
      yield* schema.update([
        schema.uiInvoiceOverall.update({
          key: "selectedCtorMirrorTab",
          value: ctx.payload,
        }),
      ]);
      yield* next();
    }
  );

export const invoiceActionFromDelphi = xThunk.create<string>(
  "/thks/delphi/invoice",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    const { action } = ctx.payload;
    switch (action) {
      case "provideNewInvoice":
      case "showDateFF": {
        const { selectedCIF } = yield* select(schema.uiInvoiceOverall.select);
        const issuers = yield* select(schema.invoiceIssuer.selectTable);
        const issuer = issuers?.[selectedCIF] || { E_ID: -1 };
        const emptyFrom = {
          FE_ID: 0, // important
          FF_ID: -1,
          INHERITED_FFID: -1,
          INHERITED_CFFID: -1,
          E_ID: issuer.E_ID,
          LA_DATA: "",
          COMMAND_STRING: "NEW",
        } as TInvoceFormCreatePayload;
        const delphi = yield* call(() => window?.delphiRequire("Jurnal_Quick"));

        yield* safe(() =>
          delphi.app.assetStore(
            JSON.stringify({
              type: showDateFFWithState.toString(),
              payload: JSON.stringify({
                value: emptyFrom,
              }),
            })
          )
        );
        break;
      }
      case "eFactDataChanged": {
        const uiState = yield* select(schema.uiInvoiceOverall.select);
        const { currentItem, selectedCIF } = uiState;
        yield* safe(
          provideNodeData.run({
            nodeId: currentItem,
            CIF: selectedCIF,
            includePrevious: false,
            stack: EComponentStack.InvoicingInKarma,
          })
        );
        break;
      }
      default:
        break;
    }
    yield* next();
  }
);

export const setCurrentInvoiceMain3 = xThunk.create<TIvFiscal>(
  "/thks/react/setCurrentInvoiceMain3",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    if (!ctx.payload) {
      yield* schema.update([
        schema.uiInvoiceCurrent.update({
          key: "currentRecord",
          value: {} as TIvFiscal,
        }),
        schema.uiInvoiceCurrent.update({
          key: "currentGroupCFFID",
          value: 0,
        }),
        schema.uiSpvMain.update({
          key: "currentRecord",
          value: {} as TRepoSPVMessages,
        }),
      ]);
      yield* next();
      return;
    }
    const spvMessages = yield* select(selectSpvDataOverall);

    const spvMessage = spvMessages.find((item) => {
      return (
        item.QRESULT_TYPE === "FACTURA TRIMISA" &&
        item.REFERINTA_INTERNA === ctx.payload.NR_FF &&
        isSameDay(
          new Date(item.REFERINTA_DATAFFID),
          new Date(ctx.payload.LA_DATA)
        )
      );
    });

    yield* schema.update([
      schema.uiInvoiceCurrent.update({
        key: "currentRecord",
        value: ctx.payload,
      }),
      schema.uiInvoiceCurrent.update({
        key: "currentGroupCFFID",
        value: ctx.payload.CFF_ID,
      }),
      schema.uiSpvMain.update({
        key: "currentRecord",
        value: spvMessage || ({} as TRepoSPVMessages),
      }),
      schema.uiCollectionCurrent.reset(),
    ]);
    yield* next();
  }
);

export const setCurrentInvoiceMain2 = xThunk.create<{ CFF_ID: number }>(
  "/thks/react/setCurrentInvoiceMain2",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update([
      schema.uiInvoiceCurrent.update({
        key: "currentRecord",
        value: {} as TIvFiscal,
      }),
      schema.uiInvoiceCurrent.update({
        key: "currentGroupCFFID",
        value: ctx.payload.CFF_ID,
      }),
      schema.uiSpvMain.update({
        key: "currentRecord",
        value: {} as TRepoSPVMessages,
      }),
      schema.uiCollectionCurrent.reset(),
    ]);
    yield* next();
  }
);

export const setCurrentInvoiceMain = xThunk.create<TIvFiscal>(
  "/thks/react/setCurrentInvoiceMain",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    if (!ctx.payload) {
      yield* schema.update([
        schema.uiInvoiceCurrent.update({
          key: "currentRecord",
          value: {} as TIvFiscal,
        }),
        schema.uiInvoiceCurrent.update({
          key: "currentGroupCFFID",
          value: 0,
        }),
        schema.uiSpvMain.update({
          key: "currentRecord",
          value: {} as TRepoSPVMessages,
        }),
      ]);
      yield* next();
      return;
    }
    const spvMessages = yield* select(selectSpvDataOverall);

    const spvMessage = spvMessages.find((item) => {
      return (
        item.QRESULT_TYPE === "FACTURA TRIMISA" &&
        item.REFERINTA_INTERNA === ctx.payload.NR_FF &&
        isSameDay(
          new Date(item.REFERINTA_DATAFFID),
          new Date(ctx.payload.LA_DATA)
        )
      );
    });

    yield* schema.update([
      schema.uiInvoiceCurrent.update({
        key: "currentRecord",
        value: ctx.payload,
      }),
      schema.uiInvoiceCurrent.update({
        key: "currentGroupCFFID",
        value: ctx.payload.CFF_ID,
      }),
      schema.uiSpvMain.update({
        key: "currentRecord",
        value: spvMessage || ({} as TRepoSPVMessages),
      }),
      schema.uiCollectionCurrent.reset(),
    ]);
    yield* next();
  }
);

export const setCurrentGroupMain = xThunk.create<number>(
  "/thks/react/setCurrentGroupMain",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update([
      schema.uiInvoiceCurrent.update({
        key: "currentRecord",
        value: {} as TIvFiscal,
      }),
      schema.uiInvoiceCurrent.update({
        key: "currentGroupCFFID",
        value: ctx.payload,
      }),
      schema.uiSpvMain.update({
        key: "currentRecord",
        value: {} as TRepoSPVMessages,
      }),
    ]);
    yield* next();
  }
);

export const fxSelectInvoiceTab = xThunk.create<EMenuCmdTab>(
  "/thks/react/selectedTab",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update([
      schema.uiJGBrowserHeader.update({
        key: "tabSelected",
        value: ctx.payload,
      }),
    ]);
    if (process.env.REACT_APP_MODE === "DELPHI") {
      if (ctx.payload === EMenuCmdTab.CMD_BGIM_INVOICE) {
        const { selectedCIF } = yield* select(schema.uiInvoiceOverall.select);

        const issuers = yield* select(schema.invoiceIssuer.selectTable);
        const issuer = issuers?.[selectedCIF] || { E_ID: -1 };
        const emptyFrom = {
          FE_ID: 0, // important
          FF_ID: -1,
          INHERITED_FFID: -1,
          INHERITED_CFFID: -1,
          E_ID: issuer.E_ID,
          LA_DATA: "",
          COMMAND_STRING: "NEW",
        };

        const delphi = yield* call(() => window?.delphiRequire("Jurnal_Quick"));

        yield* safe(() =>
          delphi.app.assetStore(
            JSON.stringify({
              type: uiSendClick_withSyncOption.toString(),
              payload: JSON.stringify({
                pushed: ctx.payload,
                value: emptyFrom,
              }),
            })
          )
        );
      }
    }
    yield* next();
  }
);

/* 
export type TCallInvoice = {
  currentGroupCFFID: number;
  currentInvoice: TIvFiscal;
  initData: TInvoceFormCreatePayload;
};
*/

/* 
NOTE:
the working model with this, (11 march 2024) is :
initialise the workflow from js.
send the data, load the DateFF.
if the user wants to initialialise the flow from delphi we send a requsest to js and initialise from here (fxSelectInvoiceTab).

then we construct an object which is parsed in FFXDM.pas :

type
  TInvoiceCommand = (icNone, icNew, icInheritCFFID, icInheritFFID, icEditInvoice, icCancelInvoice);

  TCurrentInvoice = record
    E_ID: Integer;
    FE_ID: Integer;
    FF_ID: Integer;
    INHERITED_FFID: Integer;
    INHERITED_CFFID: Integer;
    LA_DATA: TDate;
    COMMAND: TInvoiceCommand; 
  end;

  from there we initialize the invoice, using various methods.

  the main dispatcher is in Central.pas -> procedure showDateFFUsingCurrentInvoice
  but the main object is in FFXDM.pas=> CurrentInvoice: TCurrentInvoice;

*/

function parseContextEventData(
  data: any,
  action: string,
  issuers: any[],
  theToolbarState: any
) {
  const payload = {
    currentGroupCFFID: 0,
    currentInvoice: {} as TIvFiscal,
    initData: {
      FE_ID: 0,
      FF_ID: -1,
      INHERITED_FFID: 0,
      INHERITED_CFFID: 0,
      E_ID: issuers.find((x) => x.CIF === theToolbarState.selectedCIF).E_ID,
      LA_DATA: dateToSqlDate(new Date()),
      COMMAND_STRING: null,
    } as TInvoceFormCreatePayload,
  } as TCallInvoice;

  if (data?.rowType === "group") {
    payload.currentGroupCFFID = data?.data?.items?.[0]?.CFFID ?? -1;
    payload.currentInvoice = {} as TIvFiscal;

    if (action === "clone") {
      Object.assign(payload.initData, {
        FE_ID: 0,
        FF_ID: -1,
        INHERITED_FFID: 0,
        INHERITED_CFFID: data?.data?.items?.[0]?.CFFID,
        COMMAND_STRING: "INHERIT_CFFID",
      });
    }
    if (action === "edit") {
      //
      void 0;
    }
    if (action === "storno") {
      //
      void 0;
    }
  }

  if (data?.rowType === "data") {
    payload.currentGroupCFFID = data?.data?.CFF_ID ?? -1;
    payload.currentInvoice = data?.data as TIvFiscal;

    if (action === "clone") {
      Object.assign(payload.initData, {
        FE_ID: 0,
        FF_ID: 0,
        INHERITED_FFID: data?.data?.FF_ID,
        INHERITED_CFFID: 0,
        COMMAND_STRING: "INHERIT_FFID",
      });
    }
    if (action === "edit") {
      Object.assign(payload.initData, {
        FE_ID: 0,
        FF_ID: data?.data?.FF_ID,
        INHERITED_FFID: 0,
        INHERITED_CFFID: 0,
        COMMAND_STRING: "EDIT_INVOICE",
      });
    }
    if (action === "storno") {
      Object.assign(payload.initData, {
        FE_ID: 0,
        FF_ID: data?.data?.FF_ID,
        INHERITED_FFID: 0,
        INHERITED_CFFID: 0,
        COMMAND_STRING: "CANCEL_INVOICE",
      });
    }
  }
  return payload;
}

export const fxSelectInvoiceTabWithPayload = xThunk.create<{
  data: any;
  mode: "edit" | "clone" | "storno";
}>(
  "/thks/react/selectedTabWithPayload",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    // we prepare the payload for delphi
    const issuers = yield* select(select_apiProf_issuersArray);
    const theToolbarState = (yield* select(
      schema.uiInvoiceOverall.select
    )) as TUIInvoiceOverall;
    const parsedPayload = parseContextEventData(
      ctx.payload.data,
      ctx.payload.mode,
      issuers,
      theToolbarState
    );
    const { currentInvoice, currentGroupCFFID, initData } =
      parsedPayload as TCallInvoice;
    const currentFFFID = currentInvoice?.FF_ID || 0;

    // we update the javascript state,
    let spvMessage = {} as TRepoSPVMessages;

    if (currentFFFID > 0) {
      const spvMessages = yield* select(selectSpvDataOverall);
      spvMessage = spvMessages.find((item) => {
        return (
          item.QRESULT_TYPE === "FACTURA TRIMISA" &&
          item.REFERINTA_INTERNA === currentInvoice.NR_FF &&
          isSameDay(
            new Date(item.REFERINTA_DATAFFID),
            new Date(currentInvoice.LA_DATA)
          )
        );
      });
    } else {
      spvMessage = {} as TRepoSPVMessages;
    }

    yield* schema.update([
      schema.uiInvoiceCurrent.update({
        key: "currentRecord",
        value: currentInvoice,
      }),
      schema.uiInvoiceCurrent.update({
        key: "currentGroupCFFID",
        value: currentGroupCFFID,
      }),
      schema.uiSpvMain.update({
        key: "currentRecord",
        value: spvMessage,
      }),
    ]);

    //now we send to delphi our intention of action, alongside with the state needed.

    const delphi = yield* call(() => window?.delphiRequire("Jurnal_Quick"));

    yield* safe(() =>
      delphi.app.assetStore(
        JSON.stringify({
          type: uiSendClick_withSyncOption.toString(),
          payload: JSON.stringify({
            pushed: EMenuCmdTab.CMD_BGIM_INVOICE,
            value: initData,
          }),
        })
      )
    );
    yield* next();
  }
);

type TEditThisInvoice = {
  iid: number;
};
export const setEditingCollection = xThunk.create<TEditThisInvoice>(
  "/thks/react/editingCollection",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    const { iid } = ctx.payload;
    yield* schema.update([
      schema.uiCollectionCurrent.update({
        key: "iidInEdit",
        value: iid,
      }),
    ]);
    yield* next();
  }
);

export const invoiceOverallSetMainSotToSPV = xThunk.create(
  "/thks/react/invoiceOverallSetMainSotToSPV",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update([
      schema.uiInvoiceOverall.update({
        key: "mainSOT",
        value: EUIIMainSOT.SPV_OVERALL,
      }),
    ]);
    yield* next();
  }
);

export const fxSwapInvoiceView = xThunk.create(
  "/thks/react/swapInvoiceView",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    if (process.env.REACT_APP_MODE === "DELPHI") {
      const delphi = yield* call(() => window?.delphiRequire("Jurnal_Quick"));

      yield* safe(() =>
        delphi.app.assetStore(
          JSON.stringify({
            type: swapInvoiceViewMode.toString(),
            payload: "vcl",
          })
        )
      );
    }
    yield* next();
  }
);

type TSPVRT =
  | typeof SPV_RIGHT_TABS.FILE
  | typeof SPV_RIGHT_TABS.PAYMENTS
  | typeof SPV_RIGHT_TABS.OPERATIONS
  | typeof SPV_RIGHT_TABS.OPERATIONS;
export const setToolbarTopTab = xThunk.create<TSPVRT>(
  "/thks/react/setToolbarTopTab",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update([
      schema.uiSpvGrid.update({
        key: "selectedRightTab",
        value: ctx.payload,
      }),
    ]);
    yield* next();
  }
);

export const setViewToCurrentMonth = xThunk.create(
  "/thks/react/setViewToCurrentMonth",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    const treeDs = yield* select(fxInvoiceTrackTreeData);
    const latest = treeDs.at(-1);
    console.log("latest", latest);
    yield* schema.update([
      schema.uiInvoiceOverall.update({
        key: "currentItem",
        value: latest?.id,
      }),
      schema.uiInvoiceOverall.update({
        key: "includePrevious",
        value: false,
      }),
      schema.uiInvoiceOverall.update({
        key: "trackCollectionFilter",
        value: "ALL",
      }),
    ]);
    yield* next();
  }
);
